import type { ComponentProps, ReactNode } from "react";
import { useEffect, useRef, useState } from "react";
import { Link } from "@tanstack/react-router";

import { cn } from "@/common/utils";

export type PillVariant = "ink" | "glass";

// Ink matches JoinButton; glass is the system's secondary pill and lifts on
// hover like the other glass links (g-glass-hover).
const PILL =
  "inline-flex h-11 flex-none cursor-pointer items-center justify-center gap-2 rounded-full border-0 text-[15px] font-medium no-underline transition-colors disabled:cursor-default disabled:opacity-50";
const PILL_VARIANTS: Record<PillVariant, string> = {
  ink: "bg-(--g-ink) px-[26px] text-(--g-lit) hover:bg-black disabled:hover:bg-(--g-ink)",
  glass: "g-glass g-glass-hover px-[22px] text-(--g-ink)",
};

/** Pill classes for any element (a plain `<a>` for mailto: or #anchors). */
export function pillClassName(variant: PillVariant = "glass") {
  return cn(PILL, PILL_VARIANTS[variant]);
}

/**
 * A pill that navigates somewhere other than sign-up (sign-up is JoinButton).
 * Glass by default: one ink pill per section.
 */
export function PillLink({
  variant = "glass",
  className,
  ...props
}: ComponentProps<typeof Link> & { variant?: PillVariant }) {
  return <Link className={cn(pillClassName(variant), className)} {...props} />;
}

/** A pill-shaped `<button>`, for form submits and in-page actions. */
export function PillButton({
  variant = "ink",
  className,
  type = "button",
  ...props
}: ComponentProps<"button"> & { variant?: PillVariant }) {
  return (
    <button
      type={type}
      className={cn(pillClassName(variant), className)}
      {...props}
    />
  );
}

const FIELD =
  "g-glass h-12 w-full rounded-full border-0 bg-transparent px-5 text-[15px] text-(--g-ink) outline-none placeholder:text-(--g-ink-3) focus-visible:outline-1 focus-visible:outline-offset-2 focus-visible:outline-(--g-ink-3)";

/** A labelled glass text input, the hero email pill's material. */
export function TextField({
  label,
  className,
  ...props
}: ComponentProps<"input"> & { label: string }) {
  return (
    <label className={cn("block", className)}>
      <span className="mb-2 block text-[13px] text-(--g-ink-2)">{label}</span>
      <input className={FIELD} {...props} />
    </label>
  );
}

const EDGE_FADE = "32px";

/**
 * A row that scrolls sideways when it doesn't fit (pills on phones). The edge
 * with more to see fades out, so a cut-off item reads as "scroll", not as a
 * layout bug. Bleed it to the screen edge with negative margins if needed.
 */
export function ScrollRow({
  className,
  style,
  children,
  ...props
}: ComponentProps<"div">) {
  const ref = useRef<HTMLDivElement>(null);
  const [edges, setEdges] = useState({ start: false, end: false });

  useEffect(() => {
    const row = ref.current;
    if (!row) return;
    const update = () =>
      setEdges({
        start: row.scrollLeft > 1,
        end: row.scrollLeft + row.clientWidth < row.scrollWidth - 1,
      });
    update();
    row.addEventListener("scroll", update, { passive: true });
    const observer = new ResizeObserver(update);
    observer.observe(row);
    return () => {
      row.removeEventListener("scroll", update);
      observer.disconnect();
    };
  }, []);

  const mask =
    edges.start || edges.end
      ? `linear-gradient(to right, ${edges.start ? `transparent, #000 ${EDGE_FADE}` : "#000"}, ${edges.end ? `#000 calc(100% - ${EDGE_FADE}), transparent` : "#000"})`
      : undefined;

  return (
    <div
      ref={ref}
      className={cn(
        "flex overflow-x-auto [scrollbar-width:none] [&::-webkit-scrollbar]:hidden",
        className,
      )}
      style={{ ...style, maskImage: mask, WebkitMaskImage: mask }}
      {...props}
    >
      {children}
    </div>
  );
}

/**
 * One-of-many filter held in page state, as one row of pills (scrolls if it
 * must). Looks like the links `Pill` (system/links.tsx): ink when active,
 * glass otherwise. Use `Pill` when each option is its own URL.
 */
export function FilterPills<T extends string>({
  options,
  value,
  onChange,
  label,
  className,
}: {
  options: readonly { value: T; label: ReactNode }[];
  value: T;
  onChange: (value: T) => void;
  label: string;
  className?: string;
}) {
  return (
    <ScrollRow
      role="group"
      aria-label={label}
      className={cn("-mb-4 gap-2 pb-4", className)}
    >
      {options.map((option) => {
        const isActive = option.value === value;
        return (
          <button
            key={option.value}
            type="button"
            aria-pressed={isActive}
            onClick={() => onChange(option.value)}
            className={cn(
              "h-9 flex-none cursor-pointer rounded-full border-0 px-4 text-[14px] whitespace-nowrap transition-colors",
              isActive
                ? "bg-(--g-ink) text-(--g-lit)"
                : "g-glass g-glass-hover text-(--g-ink-2) hover:text-(--g-ink)",
            )}
          >
            {option.label}
          </button>
        );
      })}
    </ScrollRow>
  );
}
